import React from 'react';
import { Sparkles, Zap, Battery, Monitor, Tv } from '../lib/icons';
import ContentSlot from './ContentSlot';

const mods = [
  {
    id: 'ips',
    icon: Monitor,
    title: 'IPS Screen Upgrades',
    description: 'Replace dim, washed-out screens with vibrant IPS displays for Game Boy, GBA, DS, and more',
    devices: 'Game Boy, GBA, DS, PSP',
    color: 'bg-[#00BFFF]'
  },
  {
    id: 'battery',
    icon: Battery,
    title: 'Rechargeable Battery Mods',
    description: 'Convert AA battery systems to modern USB-C rechargeable batteries',
    devices: 'Game Boy, GBA, GBA SP',
    color: 'bg-[#39FF14]'
  },
  {
    id: 'hdmi',
    icon: Tv,
    title: 'HDMI Mods',
    description: 'Internal HDMI installation for crisp digital output on modern TVs. No more blurry composite cables',
    devices: 'N64, SNES, NES, PS1, PS2',
    color: 'bg-[#FF1744]'
  },
  {
    id: 'shell',
    icon: Zap,
    title: 'Shell Replacements',
    description: 'Custom shells in various colors, transparent designs, and special editions',
    devices: 'All handheld consoles',
    color: 'bg-[#FF1744]'
  },
  {
    id: 'custom',
    icon: Sparkles,
    title: 'Full Custom Builds',
    description: 'Complete custom builds with multiple mods, custom artwork, and unique designs',
    devices: 'All systems',
    color: 'bg-[#00BFFF]'
  }
];

const RetroMods = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-black to-[#0D0D0D] text-[#E0E0E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#FF1744]/10 border border-[#FF1744]/30 rounded-full px-6 py-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#FF1744]" />
            <span className="text-[#FF1744] font-semibold">
              <ContentSlot slotId="retro-badge" fallback="RETRO GAMING" contentType="label" pageSection="retro-mods" label="Section Badge" />
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-4">
            <ContentSlot slotId="retro-heading" fallback="Retro Console Mods & Upgrades" contentType="heading" pageSection="retro-mods" label="Section Heading" />
          </h2>
          <ContentSlot
            slotId="retro-subtitle"
            fallback="Breathe new life into your classic gaming consoles with modern upgrades. From IPS screens to rechargeable batteries, we specialize in retro console modifications."
            contentType="paragraph"
            as="p"
            pageSection="retro-mods"
            label="Section Subtitle"
            className="text-xl text-[#B0B0B0] max-w-3xl mx-auto"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {mods.map((mod) => (
            <div
              key={mod.id}
              className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-[#00BFFF]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#00BFFF]/20"
            >
              <div className={`${mod.color}/20 p-3 rounded-lg w-fit mb-4`}>
                <mod.icon className={`h-8 w-8 ${mod.color.replace('bg-', 'text-')}`} />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <ContentSlot slotId={`retro-mod-${mod.id}-title`} fallback={mod.title} contentType="heading" pageSection="retro-mods" label={`Mod: ${mod.title}`} />
              </h3>
              <p className="text-[#B0B0B0] mb-4">
                <ContentSlot slotId={`retro-mod-${mod.id}-desc`} fallback={mod.description} contentType="paragraph" pageSection="retro-mods" label={`Mod Description: ${mod.title}`} />
              </p>
              <div className="bg-gray-800/50 rounded-lg px-3 py-2 inline-block">
                <p className="text-sm text-[#00BFFF]">{mod.devices}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-8 border border-gray-800">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">
                <ContentSlot slotId="retro-systems-heading" fallback="Popular Retro Systems We Mod" contentType="subheading" pageSection="retro-mods" label="Systems List Heading" />
              </h3>
              <div className="space-y-3">
                {[
                  'NES (Nintendo Entertainment System)',
                  'Super Nintendo (SNES)',
                  'Nintendo 64',
                  'Game Boy (DMG, Pocket, Color)',
                  'Game Boy Advance (GBA, GBA SP)',
                  'Nintendo DS & DS Lite',
                  'PlayStation 1 & 2',
                  'PlayStation Portable (PSP)',
                  'Sega Genesis & Game Gear',
                  'Retro Handhelds'
                ].map((system, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#39FF14] rounded-full"></div>
                    <span className="text-[#E0E0E0]">{system}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#FF1744]/20 to-[#00BFFF]/20 rounded-xl p-6 border border-[#00BFFF]/30">
              <h4 className="text-2xl font-bold mb-4">
                <ContentSlot slotId="retro-how-heading" fallback="How It Works" contentType="subheading" pageSection="retro-mods" label="How It Works Heading" />
              </h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-[#39FF14] text-black font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">1</div>
                  <p className="text-[#E0E0E0]">
                    <ContentSlot slotId="retro-step-1" fallback="Bring in your console or let us source one for you" contentType="paragraph" pageSection="retro-mods" label="Step 1" />
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-[#39FF14] text-black font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">2</div>
                  <p className="text-[#E0E0E0]">
                    <ContentSlot slotId="retro-step-2" fallback="Choose your mods and customizations" contentType="paragraph" pageSection="retro-mods" label="Step 2" />
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-[#39FF14] text-black font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">3</div>
                  <p className="text-[#E0E0E0]">
                    <ContentSlot slotId="retro-step-3" fallback="We'll professionally install all upgrades" contentType="paragraph" pageSection="retro-mods" label="Step 3" />
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-[#39FF14] text-black font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1">4</div>
                  <p className="text-[#E0E0E0]">
                    <ContentSlot slotId="retro-step-4" fallback="Pick up your upgraded retro console" contentType="paragraph" pageSection="retro-mods" label="Step 4" />
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <a
              href="/#contact"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF1744] to-[#00BFFF] hover:from-[#00BFFF] hover:to-[#39FF14] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Sparkles className="h-5 w-5" />
              <span><ContentSlot slotId="retro-cta" fallback="Start Your Retro Mod Project" contentType="button" pageSection="retro-mods" label="CTA Button" /></span>
            </a>
            <p className="text-[#B0B0B0] text-sm mt-3">
              <ContentSlot slotId="retro-cta-note" fallback="Each mod is custom-quoted based on your requirements." contentType="label" pageSection="retro-mods" label="CTA Note" />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RetroMods;
